import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../services/supabase';
import toast, { Toaster } from 'react-hot-toast';
import { ArrowLeftIcon } from '@heroicons/react/24/solid';

const SUGGESTED_AMOUNTS = [5, 10, 25, 50];
const PROCESSING_FEE_RATE = 0.029;
const PROCESSING_FEE_FLAT = 0.30;

const SupportPage: React.FC = () => {
  const { username } = useParams<{ username: string }>();
  const navigate = useNavigate();

  const [talent, setTalent] = useState<any>(null);
  const [bioSettings, setBioSettings] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const [amount, setAmount] = useState('');
  const [personalMessage, setPersonalMessage] = useState('');
  const [isMonthly, setIsMonthly] = useState(false);
  const [email, setEmail] = useState('');
  const [emailValid, setEmailValid] = useState(false);
  // See ShoutOutProfilePage for rationale: track whether the user has
  // committed their email (blurred / Enter / Continue tap) so we don't show
  // an "invalid" error mid-typing.
  const [emailTouched, setEmailTouched] = useState(false);
  const [savedCards, setSavedCards] = useState<any[]>([]);
  const [selectedCardId, setSelectedCardId] = useState<number | null>(null);
  const [lpCustomerId, setLpCustomerId] = useState<number | null>(null);
  const [processing, setProcessing] = useState(false);
  const [userCredits, setUserCredits] = useState(0);
  const [useCredits, setUseCredits] = useState(false);
  const [isVerified, setIsVerified] = useState(false);

  // Fortis
  const iframeContainerRef = useRef<HTMLDivElement>(null);
  const [commerceInstance, setCommerceInstance] = useState<any>(null);
  const [isPaymentReady, setIsPaymentReady] = useState(false);
  const [useNewCard, setUseNewCard] = useState(false);
  const successHandledRef = useRef(false);
  const fortisInitRef = useRef(false);
  const amountRef = useRef({ totalCharge: 0, amountToPay: 0, isMonthly: false, donationAmount: 0, personalMessage: '' });

  // OTP
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otpPhone, setOtpPhone] = useState('');
  const [otpPhoneHint, setOtpPhoneHint] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpNeedsPhone, setOtpNeedsPhone] = useState(false);

  // Fan Shoutouts extras
  const [searchParams] = useSearchParams();
  const skipMessage = searchParams.get('skipMessage') === 'true';
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [supporterMessages, setSupporterMessages] = useState<any[]>([]);

  // External donation links (collapsed from bio page when 2+ exist)
  const [donateLinks, setDonateLinks] = useState<Array<{ id: string; title?: string | null; url: string }>>([]);

  useEffect(() => {
    const load = async () => {
      if (!username) return;
      let tp: any = null;
      const { data: byUsername } = await supabase.from('talent_profiles').select('id, user_id, username, full_name, temp_full_name, temp_avatar_url, support_enabled').eq('username', username).single();
      if (byUsername) tp = byUsername;
      else if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(username)) { const { data: byId } = await supabase.from('talent_profiles').select('id, user_id, username, full_name, temp_full_name, temp_avatar_url, support_enabled').eq('id', username).single(); tp = byId; }
      if (!tp || !tp.support_enabled) { navigate(`/${username}`); return; }
      setTalent(tp);
      const { data: bs } = await supabase.from('bio_settings').select('*').eq('talent_id', tp.id).single();
      setBioSettings(bs);

      supabase
        .from('donation_messages')
        .select('donor_name, message, amount, is_monthly, is_anonymous, created_at')
        .eq('talent_id', tp.id)
        .not('message', 'is', null)
        .order('created_at', { ascending: false })
        .limit(50)
        .then(({ data }) => { if (data) setSupporterMessages(data); });

      supabase
        .from('bio_links')
        .select('id, title, url')
        .eq('talent_id', tp.id)
        .eq('link_type', 'donate')
        .eq('is_active', true)
        .order('display_order')
        .then(({ data }) => {
          if (data) setDonateLinks((data as any[]).filter(l => l.url));
        });

      const savedEmail = localStorage.getItem('bio_subscriber_email');
      const verified = localStorage.getItem('bio_account_verified') === 'true';
      if (savedEmail) {
        setEmail(savedEmail);
        setEmailValid(true);
        if (verified) {
          setIsVerified(true);
          loadCards(savedEmail);
        }
      }
      setLoading(false);
    };
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [username, navigate]);

  const loadCards = async (em: string) => {
    try {
      const { data } = await supabase.functions.invoke('lunarpay-api', { body: { action: 'get-payment-methods', email: em } });
      if (data?.paymentMethods?.length > 0) {
        setSavedCards(data.paymentMethods);
        setSelectedCardId(data.paymentMethods.find((c: any) => c.isDefault)?.id || data.paymentMethods[0].id);
      }
      const { data: cust } = await supabase.functions.invoke('lunarpay-api', { body: { action: 'find-or-create-customer', email: em } });
      if (cust?.customerId) setLpCustomerId(cust.customerId);
      const { data: acct } = await supabase.functions.invoke('lunarpay-api', { body: { action: 'get-account', email: em } });
      if (acct?.user?.credits) setUserCredits(parseFloat(acct.user.credits) || 0);
    } catch {}
  };

  const displayName = bioSettings?.display_name || talent?.full_name || talent?.temp_full_name || 'Creator';
  const firstName = displayName.startsWith('The ') || displayName.startsWith('Chicks') ? displayName : displayName.split(' ')[0];
  const profileImage = bioSettings?.profile_image_url || talent?.temp_avatar_url;
  const gradStart = bioSettings?.gradient_start || '#0a0a0a';
  const gradEnd = bioSettings?.gradient_end || '#1a1a2e';
  const accentColor = bioSettings?.button_color || '#22c55e';

  // Auto contrast: detect light backgrounds so white text doesn't disappear on
  // pale gradients (e.g. pastel pinks / lavenders). Use the gradient average so
  // we account for both stops, not just the top.
  const hexToRgb = (hex: string) => {
    const m = hex?.replace('#', '').match(/^([0-9a-f]{3}|[0-9a-f]{6})$/i);
    if (!m) return { r: 10, g: 10, b: 10 };
    const h = m[1].length === 3 ? m[1].split('').map(c => c + c).join('') : m[1];
    return { r: parseInt(h.slice(0, 2), 16), g: parseInt(h.slice(2, 4), 16), b: parseInt(h.slice(4, 6), 16) };
  };
  const bgTop = hexToRgb(gradStart);
  const bgBot = hexToRgb(gradEnd);
  const relLum = ({ r, g, b }: { r: number; g: number; b: number }) =>
    (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  const topLum = relLum(bgTop);
  const botLum = relLum(bgBot);
  // Weight the top stop heavily: the donation form is anchored at the top,
  // so a flat average falsely classifies light→dark gradients (e.g.
  // #f1c9fe → #302832) as dark even though everything the user reads sits
  // over the near-white portion. Treat as light if the top itself is light
  // OR the 80/20 weighted mix is above the threshold.
  const weightedLum = topLum * 0.8 + botLum * 0.2;
  const isLightBg = topLum > 0.6 || weightedLum > 0.55;

  // Keep the browser tab title in sync with the creator (e.g. "Support Lydia | ShoutOut Fans").
  useEffect(() => {
    if (!talent) return;
    const pageTitle = `Support ${firstName} | ShoutOut Fans`;
    document.title = pageTitle;
    const setMeta = (property: string, content: string, isName = false) => {
      const attr = isName ? 'name' : 'property';
      let el = document.querySelector(`meta[${attr}="${property}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, property);
        document.head.appendChild(el);
      }
      el.content = content;
    };
    const description = `Support ${displayName} with a donation`;
    const avatar = profileImage || '';
    const pageUrl = `https://shoutout.fans/${username}/support`;
    setMeta('og:title', pageTitle);
    setMeta('og:description', description);
    setMeta('og:site_name', 'ShoutOut Fans');
    setMeta('og:type', 'profile');
    setMeta('og:url', pageUrl);
    if (avatar) setMeta('og:image', avatar);
    setMeta('twitter:card', 'summary', true);
    setMeta('twitter:title', pageTitle, true);
    setMeta('twitter:description', description, true);
    if (avatar) setMeta('twitter:image', avatar, true);
    setMeta('description', description, true);
    return () => { document.title = 'ShoutOut Fans'; };
  }, [talent, firstName, displayName, profileImage, username]);

  const donationAmount = parseFloat(amount) || 0;
  const processingFee = donationAmount > 0 ? Math.round((donationAmount * PROCESSING_FEE_RATE + PROCESSING_FEE_FLAT) * 100) / 100 : 0;
  const totalCharge = Math.round((donationAmount + processingFee) * 100) / 100;
  const creditsAvailable = (!isMonthly && useCredits && isVerified) ? Math.min(userCredits, totalCharge) : 0;
  const amountToPay = Math.max(0, totalCharge - creditsAvailable);
  amountRef.current = { totalCharge, amountToPay, isMonthly, donationAmount, personalMessage };

  // Email commit (blur / Enter / Continue): validate and check for existing user
  const handleEmailBlur = async () => {
    setEmailTouched(true);
    const trimmed = email.trim();
    if (trimmed !== email) setEmail(trimmed);
    const v = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(trimmed);
    setEmailValid(v);
    if (!v) return;
    try {
      const { data } = await supabase.functions.invoke('send-registration-otp', { body: { email: email.toLowerCase().trim(), checkEmailOnly: true } });
      if (data?.sentToExistingPhone) {
        setOtpPhoneHint(data.phoneHint || '');
        setOtpPhone(data.phone || '');
        setOtpSent(true);
        setOtpNeedsPhone(false);
        setShowOtpModal(true);
        return;
      }
    } catch {}
  };

  // OTP handlers
  const handleOtpSend = async () => {
    if (!otpPhone.trim()) { toast.error('Enter your phone number'); return; }
    setOtpLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('send-registration-otp', {
        body: { email: email.toLowerCase().trim(), phone: otpPhone },
      });
      if (error) throw error;
      setOtpPhoneHint(data?.phoneHint || otpPhone.slice(-4));
      setOtpSent(true);
      toast.success('Code sent!');
    } catch (err: any) { toast.error(err?.message || 'Failed'); }
    finally { setOtpLoading(false); }
  };

  const handleOtpVerify = async () => {
    if (otpCode.length < 4) { toast.error('Enter the code'); return; }
    setOtpLoading(true);
    try {
      const { error } = await supabase.functions.invoke('verify-registration-otp', {
        body: { email: email.toLowerCase().trim(), phone: otpPhone, code: otpCode },
      });
      if (error) throw error;
      toast.success('Verified!');
      localStorage.setItem('bio_subscriber_email', email.toLowerCase().trim());
      localStorage.setItem('bio_account_verified', 'true');
      setEmailValid(true);
      setIsVerified(true);
      await loadCards(email);
      setShowOtpModal(false);
    } catch (err: any) { toast.error(err?.message || 'Invalid code'); }
    finally { setOtpLoading(false); }
  };

  // Fortis init — EXACT same pattern as ShoutOutProfilePage
  const initializeFortis = useCallback(async () => {
    if (!email || !talent || fortisInitRef.current) return;
    fortisInitRef.current = true;
    try {
      const { data: intentionData, error: intentionError } = await supabase.functions.invoke('lunarpay-intention', {});
      if (intentionError || !intentionData?.clientToken) throw new Error('Failed to create payment intention');

      const { clientToken } = intentionData;

      const waitForCommerce = () =>
        new Promise<void>((resolve, reject) => {
          if ((window as any).Commerce?.elements) { resolve(); return; }
          if (!document.querySelector('script[src*="commercejs"]')) {
            const script = document.createElement('script');
            script.src = 'https://js.fortis.tech/commercejs-v1.0.0.min.js';
            script.async = true;
            document.body.appendChild(script);
          }
          let attempts = 0;
          const interval = setInterval(() => {
            attempts++;
            if ((window as any).Commerce?.elements) { clearInterval(interval); resolve(); }
            if (attempts > 50) { clearInterval(interval); reject(new Error('Commerce JS failed')); }
          }, 100);
        });
      await waitForCommerce();

      const ElementsCtor = (window as any).Commerce?.elements;
      if (!ElementsCtor) throw new Error('Commerce elements not available');

      const elements = new ElementsCtor(clientToken);

      successHandledRef.current = false;

      const handlePaymentSuccess = async (payload: any) => {
        if (successHandledRef.current) return;
        successHandledRef.current = true;
        setProcessing(true);
        try {
          const ticketId = payload?.ticket?.id || payload?.data?.id || payload?.id;
          if (!ticketId) throw new Error('No payment ticket');
          await processPaymentWithTicket(ticketId.toString());
        } catch (e: any) {
          toast.error(e.message || 'Payment failed');
          successHandledRef.current = false;
        } finally {
          setProcessing(false);
        }
      };

      elements.eventBus.on('ready', () => setIsPaymentReady(true));
      elements.eventBus.on('payment_success', handlePaymentSuccess);
      elements.eventBus.on('success', handlePaymentSuccess);
      elements.eventBus.on('done', handlePaymentSuccess);
      elements.eventBus.on('payment_error', () => { successHandledRef.current = false; toast.error('Payment failed'); });
      elements.eventBus.on('error', () => { successHandledRef.current = false; toast.error('Payment error'); });

      elements.create({
        container: '#support-payment-container',
        theme: 'dark',
        environment: 'production',
        view: 'default',
        language: 'en-us',
        defaultCountry: 'US',
        floatingLabels: true,
        showReceipt: false,
        showSubmitButton: false,
        showValidationAnimation: true,
        hideAgreementCheckbox: true,
        hideTotal: true,
        appearance: {
          colorBackground: gradStart,
          colorButtonSelectedBackground: accentColor,
          colorButtonSelectedText: '#ffffff',
          colorText: isLightBg ? '#0a0a0a' : '#ffffff',
          fontFamily: 'SourceSans',
          fontSize: '16px',
          borderRadius: '8px',
        },
      });

      setCommerceInstance(elements);
    } catch (e: any) {
      console.error('Fortis init error:', e);
      fortisInitRef.current = false;
      if (!isPaymentReady) toast.error('Failed to load payment form');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email, talent, gradStart, accentColor]);

  // Process payment after Fortis gives ticket (reads from ref to avoid stale closure)
  const processPaymentWithTicket = async (ticketId: string) => {
    const { totalCharge: tc, amountToPay: atp, isMonthly: monthly, donationAmount: da, personalMessage: pm } = amountRef.current;
    const totalCents = Math.round(tc * 100);
    const payCents = Math.round(atp * 100);
    if (payCents < 1) throw new Error('Invalid amount');

    // Monthly: redirect to LunarPay hosted checkout (handles card + subscription)
    if (monthly) {
      const { data: sessionData, error: sessionErr } = await supabase.functions.invoke('lunarpay-api', {
        body: {
          action: 'create-subscription',
          amount: da,
          customerEmail: email.toLowerCase(),
          description: `Monthly support for ${displayName}`,
          frequency: 'monthly',
          successUrl: `https://shoutout.fans/${username}`,
        },
      });
      if (sessionErr || !sessionData?.subscription?.url) throw new Error(sessionData?.error || 'Failed to create subscription');
      window.location.href = sessionData.subscription.url;
      return;
    }

    // One-time: charge via Fortis ticket
    const { data: lpResult, error: lpError } = await supabase.functions.invoke('lunarpay-api', {
      body: { action: 'process-payment', email: email.toLowerCase(), ticketId, amount: payCents, description: `Support ${displayName}` },
    });
    if (lpError || !lpResult?.chargeId) throw new Error(lpResult?.error || 'Payment processing failed');

    const txnId =
      lpResult.transactionId ||
      (lpResult.chargeId != null ? `LP_${lpResult.chargeId}` : null);
    if (!txnId) throw new Error('Charge succeeded but no transaction id returned');
    await createDonationOrder(totalCents, 0, txnId, false, da, pm);
  };

  // Pay with saved card
  const handlePayWithSavedCard = async () => {
    if (!selectedCardId || !lpCustomerId) return;
    setProcessing(true);
    try {
      const totalCents = Math.round(totalCharge * 100);
      const payCents = Math.round(amountToPay * 100);

      // Capture a unique payment_transaction_id from whichever path runs.
      // The old code passed the literal 'SAVED_CARD' which collided as
      // soon as a second saved-card supporter donated, then routed the
      // duplicate insert into a stranger's order via idempotency.
      let txnId: string | null = null;

      if (isMonthly) {
        const { data: sessionData, error: sessionErr } = await supabase.functions.invoke('lunarpay-api', {
          body: {
            action: 'create-subscription',
            amount: donationAmount,
            customerEmail: email.toLowerCase(),
            description: `Monthly support for ${displayName}`,
            frequency: 'monthly',
            successUrl: `https://shoutout.fans/${username}`,
          },
        });
        if (sessionErr || !sessionData?.subscription?.url) throw new Error('Subscription creation failed');
        window.location.href = sessionData.subscription.url;
        return;
      } else {
        const { data: chargeResult, error: chargeErr } = await supabase.functions.invoke('lunarpay-api', {
          body: { action: 'charge', customerId: lpCustomerId, paymentMethodId: selectedCardId, amount: payCents, description: `Support ${displayName}` },
        });
        if (chargeErr || !chargeResult?.charge) throw new Error('Payment failed');
        const ch = chargeResult.charge;
        txnId =
          ch?.fortisTransactionId ||
          (ch?.id != null ? `LP_${ch.id}` : null);
      }

      if (!txnId) throw new Error('Payment succeeded but no transaction id returned');

      await createDonationOrder(totalCents, 0, txnId, isMonthly, donationAmount, personalMessage);
    } catch (e: any) { toast.error(e.message || 'Payment failed'); }
    finally { setProcessing(false); }
  };

  // Create donation order + notifications (params avoid stale closure from Fortis callback)
  const createDonationOrder = async (totalCents: number, adminCents: number, txId: string, monthly: boolean, amt: number, msg: string) => {
    if (creditsAvailable > 0) {
      const { data: acct } = await supabase.functions.invoke('lunarpay-api', { body: { action: 'get-account', email } });
      if (acct?.user?.id) {
        await supabase.functions.invoke('lunarpay-api', {
          body: { action: 'deduct-credits', userId: acct.user.id, amount: creditsAvailable, notes: `Support donation to ${displayName}` },
        });
      }
    }

    const { data: orderId } = await supabase.rpc('create_guest_order', {
      p_guest_email: email.toLowerCase(), p_talent_id: talent.id,
      p_occasion: monthly ? 'monthly_support' : 'one_time_support',
      p_amount: totalCents, p_admin_fee: adminCents,
      p_fulfillment_deadline: new Date(2099, 0, 1).toISOString(),
      p_payment_transaction_id: txId,
      p_payment_transaction_payload: { type: 'donation', monthly, amount: amt },
      p_promo_source: 'fans', p_is_express_delivery: false,
    });
    if (orderId) {
      await supabase.functions.invoke('lunarpay-api', {
        body: { action: 'update-order', orderId, updates: { service_type: 'donation', status: 'completed' } },
      }).catch(() => {});
    }

    if (msg && msg.trim()) {
      await supabase.functions.invoke('lunarpay-api', {
        body: {
          action: 'insert-donation-message',
          talentId: talent.id,
          donorEmail: email.toLowerCase(),
          donorName: email.split('@')[0],
          message: msg.trim(),
          amount: amt,
          isMonthly: monthly,
          orderId: orderId || null,
          isAnonymous,
        },
      }).catch(() => {});
    }

    try {
      const photoHtml = profileImage
        ? `<img src="${profileImage}" alt="${displayName}" width="80" height="80" style="width:80px;height:80px;border-radius:50%;object-fit:cover;border:3px solid rgba(255,255,255,0.2);">`
        : '';
      const photoSquare = profileImage
        ? `<img src="${profileImage}" alt="${displayName}" width="120" height="120" style="width:120px;height:120px;object-fit:cover;display:block;">`
        : '';
      const donationReceiptHtml = `<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1.0"></head>
<body style="margin:0;padding:0;background-color:#0f0f1a;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;">
<table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="background-color:#0f0f1a;">
<tr><td align="center" style="padding:40px 20px;">
<table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="max-width:600px;">
  <tr><td align="center" style="padding-bottom:30px;">
    <img src="https://utafetamgwukkbrlezev.supabase.co/storage/v1/object/public/platform-assets/logos/logowhite.png" alt="ShoutOut" height="40" style="height:40px;">
  </td></tr>
  <tr><td align="center" style="padding-bottom:30px;">
    ${photoHtml}
    <h1 style="color:#ffffff;font-size:24px;font-weight:600;margin:16px 0 0 0;">${displayName}</h1>
  </td></tr>
  <tr><td style="background:rgba(255,255,255,0.05);border-radius:16px;padding:32px;border:1px solid rgba(255,255,255,0.1);">
    <p style="color:#ffffff;font-size:22px;font-weight:700;margin:0 0 16px 0;">Thank you! 💚</p>
    <p style="color:rgba(255,255,255,0.8);font-size:16px;line-height:1.6;margin:0 0 20px 0;">
      Your ${monthly ? 'monthly' : 'one-time'} donation of <strong style="color:#ffffff;">$${amt.toFixed(2)}</strong> to <strong style="color:#ffffff;">${displayName}</strong> has been received.
    </p>
    ${monthly ? '<div style="background:rgba(34,197,94,0.1);border:1px solid rgba(34,197,94,0.2);border-radius:12px;padding:16px;margin:0 0 24px 0;"><p style="color:#4ade80;font-weight:600;font-size:15px;margin:0;">🎁 You\'ve unlocked 10% off ShoutOuts!</p></div>' : ''}
    <p style="color:rgba(255,255,255,0.8);font-size:16px;line-height:1.6;margin:0 0 24px 0;">
      Want a <strong style="color:#ffffff;">personalized video message</strong> from ${displayName}? Order a ShoutOut for yourself or as a unique gift!
    </p>
    <div style="text-align:center;">
      <a href="https://shoutout.fans/${username}/shoutout" style="display:inline-block;background:linear-gradient(135deg,#7c3aed 0%,#6366f1 100%);color:#ffffff;text-decoration:none;padding:14px 32px;border-radius:12px;font-weight:600;font-size:16px;">
        Get a ShoutOut from ${displayName} →
      </a>
    </div>
    <div style="text-align:center;margin-top:16px;">
      <a href="https://shoutout.fans/${username}" style="display:inline-block;background:rgba(255,255,255,0.1);color:#ffffff;text-decoration:none;padding:12px 28px;border-radius:12px;font-weight:600;font-size:15px;border:1px solid rgba(255,255,255,0.2);">
        💬 Send ${displayName} a Message
      </a>
    </div>
  </td></tr>
  <tr><td style="padding:24px 0;"><div style="border-top:1px solid rgba(255,255,255,0.1);"></div></td></tr>
  <tr><td style="padding-top:16px;">
    <a href="https://shoutout.fans/${username}/shoutout" target="_blank" style="text-decoration:none;display:block;">
      <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="background:linear-gradient(135deg,#1e3a5f 0%,#4c1d95 100%);background-color:#2d2a6e;border-radius:16px;overflow:hidden;">
        <tr>
          <td width="120" style="vertical-align:middle;background-color:transparent;">
            <div style="width:120px;height:120px;overflow:hidden;">${photoSquare}</div>
          </td>
          <td style="padding:20px;vertical-align:middle;background-color:transparent;">
            <div style="color:#93c5fd;font-size:15px;font-weight:600;">Get a Personalized Video ShoutOut</div>
            <div style="color:#ffffff;font-size:14px;margin-top:6px;">From ${displayName}</div>
          </td>
        </tr>
      </table>
    </a>
  </td></tr>
  <tr><td style="padding-top:32px;text-align:center;">
    <div style="border-top:1px solid rgba(255,255,255,0.1);padding-top:24px;">
      <p style="color:rgba(255,255,255,0.4);font-size:12px;margin:0 0 16px 0;">
        <a href="https://shoutout.us/privacy" style="color:rgba(255,255,255,0.4);text-decoration:underline;">Privacy Policy</a>
      </p>
      <p style="color:rgba(255,255,255,0.4);font-size:11px;margin:0 0 16px 0;opacity:0.7;">
        ShoutOut, LLC &bull; 1201 N Riverfront Blvd Ste 100, Dallas, TX 75207
      </p>
      <div style="margin-top:8px;">
        <a href="https://shoutout.us/creators" style="text-decoration:none;opacity:0.6;">
          <span style="color:rgba(255,255,255,0.4);font-size:11px;margin-right:8px;vertical-align:middle;">Powered by</span>
          <img src="https://utafetamgwukkbrlezev.supabase.co/storage/v1/object/public/platform-assets/logos/logowhite.png" alt="ShoutOut" height="24" style="height:24px;vertical-align:middle;">
        </a>
      </div>
    </div>
  </td></tr>
</table>
</td></tr>
</table>
</body></html>`;
      await supabase.functions.invoke('send-mailgun', {
        body: {
          to: email,
          subject: `Thank you for supporting ${displayName}!`,
          html: donationReceiptHtml,
        }
      });
      await fetch('https://hooks.zapier.com/hooks/catch/25578725/u0ptrak/', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customer_email: email, customer_name: email.split('@')[0], talent_name: displayName, talent_id: talent.id, amount: amt, type: monthly ? 'monthly' : 'one_time', source: 'shoutout.fans' }),
      }).catch(() => {});
      if (talent.user_id) {
        // Only text talent on first donation from this user
        const { data: priorDonations } = await supabase.functions.invoke('lunarpay-api', { body: { action: 'get-orders', email } });
        const hasPriorDonation = (priorDonations?.orders || []).some((o: any) => o.talent_id === talent.id && o.service_type === 'donation' && o.id !== orderId);
        if (!hasPriorDonation) {
          const { data: tu } = await supabase.from('users').select('phone').eq('id', talent.user_id).single();
          if (tu?.phone) {
            const msg = monthly
              ? `${email.split('@')[0]} just set up a monthly donation for $${amt.toFixed(2)}! 💚 (We won't text you if they donate again.)`
              : `${email.split('@')[0]} just donated $${amt.toFixed(2)}! 💚 (We won't text you if they donate again.)`;
            await supabase.functions.invoke('send-sms', { body: { to: tu.phone, message: msg, recipientType: 'talent' } });
          }
        }
      }
    } catch {}

    // Ensure donor becomes a fan/follower
    try {
      const { data: existingUser } = await supabase
        .from('users')
        .select('id')
        .ilike('email', email.trim())
        .single();
      if (existingUser) {
        await supabase.from('talent_followers').upsert({
          user_id: existingUser.id,
          talent_id: talent.id,
        }, { onConflict: 'user_id,talent_id', ignoreDuplicates: true });

        supabase.functions.invoke('capture-lead', {
          body: { email: email.toLowerCase(), source: 'bio_page', talent_slug: talent.username || talent.id },
        }).catch(() => {});
      }
    } catch {}

    localStorage.setItem('bio_subscriber_email', email.toLowerCase());
    localStorage.setItem('bio_account_verified', 'true');
    toast.success(`Thank you for supporting ${firstName}!`);
    setTimeout(() => navigate(`/${username}`), 2000);
  };

  // Init Fortis when email is valid, amount selected, and no saved cards shown
  useEffect(() => {
    if (emailValid && donationAmount >= 1 && !commerceInstance && !showOtpModal && (savedCards.length === 0 || useNewCard)) {
      initializeFortis();
    }
  }, [emailValid, donationAmount, commerceInstance, showOtpModal, savedCards.length, useNewCard, initializeFortis]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500" />
      </div>
    );
  }

  const showFortisForm = emailValid && amountToPay > 0 && (savedCards.length === 0 || useNewCard);

  return (
    <div
      className="min-h-screen"
      data-light-bg={isLightBg ? 'true' : 'false'}
      style={{ background: `linear-gradient(to bottom, ${gradStart}, ${gradEnd})` }}
    >
      {isLightBg && (
        <style>{`
          /* Primary text is pure black for strong, consistent contrast */
          [data-light-bg="true"] .text-white,
          [data-light-bg="true"] .text-gray-300 { color: #0a0a0a !important; }
          /* Muted copy: one opacity for a consistent look across subtitles/helpers */
          [data-light-bg="true"] .text-gray-400,
          [data-light-bg="true"] .text-gray-500 { color: rgba(0,0,0,0.7) !important; }
          [data-light-bg="true"] .text-gray-600 { color: rgba(0,0,0,0.55) !important; }
          /* Accents remain green/blue but darker so they read on light bg */
          [data-light-bg="true"] .text-green-400 { color: #15803d !important; }
          [data-light-bg="true"] .text-blue-400 { color: #1d4ed8 !important; }
          /* Preserve white text on solid colored buttons/badges */
          [data-light-bg="true"] .bg-green-500.text-white,
          [data-light-bg="true"] .bg-green-600.text-white,
          [data-light-bg="true"] .bg-green-700.text-white,
          [data-light-bg="true"] .bg-blue-500.text-white,
          [data-light-bg="true"] .bg-blue-600.text-white,
          [data-light-bg="true"] .hover\\:bg-green-600.text-white,
          [data-light-bg="true"] .hover\\:bg-green-700.text-white,
          [data-light-bg="true"] .hover\\:bg-blue-700.text-white { color: #ffffff !important; }
          /* Panel / input backgrounds (flip translucent white to translucent black).
             On pale pastel gradients .bg-white/5 is basically invisible, so we
             push the alpha up enough that every card/input reads as a distinct
             surface without turning the page muddy. */
          [data-light-bg="true"] .bg-white\\/5 { background-color: rgba(0,0,0,0.12) !important; }
          [data-light-bg="true"] .bg-white\\/10 { background-color: rgba(0,0,0,0.16) !important; }
          [data-light-bg="true"] .border-white\\/10 { border-color: rgba(0,0,0,0.25) !important; }
          [data-light-bg="true"] .border-white\\/20 { border-color: rgba(0,0,0,0.35) !important; }
          [data-light-bg="true"] .border-white\\/40 { border-color: rgba(0,0,0,0.5) !important; }
          [data-light-bg="true"] .placeholder-gray-500::placeholder,
          [data-light-bg="true"] input::placeholder,
          [data-light-bg="true"] textarea::placeholder { color: rgba(0,0,0,0.55) !important; opacity: 1; }
          [data-light-bg="true"] .hover\\:bg-white\\/10:hover { background-color: rgba(0,0,0,0.2) !important; }
          [data-light-bg="true"] .hover\\:text-white:hover { color: #000 !important; }
          [data-light-bg="true"] .focus\\:border-white\\/40:focus { border-color: rgba(0,0,0,0.55) !important; }
          /* Selected / ring states should read on light bg too */
          [data-light-bg="true"] .ring-white\\/40 { --tw-ring-color: rgba(0,0,0,0.4) !important; }
          [data-light-bg="true"] .ring-white\\/60 { --tw-ring-color: rgba(0,0,0,0.55) !important; }
          /* Divider lines that use white/10 etc. */
          [data-light-bg="true"] .divide-white\\/10 > :not([hidden]) ~ :not([hidden]) { border-color: rgba(0,0,0,0.18) !important; }
        `}</style>
      )}
      <Toaster position="top-center" />
      <div className="max-w-lg mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button onClick={() => navigate(`/${username}`)} className="flex items-center gap-2 text-gray-400 hover:text-white">
            <ArrowLeftIcon className="h-4 w-4" /><span className="text-sm">Back</span>
          </button>
          <img src="/shoutoutwhite.png" alt="ShoutOut" className="h-5 opacity-60" />
        </div>

        {/* Profile */}
        <div className="text-center mb-6">
          {profileImage && <img src={profileImage} alt="" className="w-20 h-20 rounded-full mx-auto mb-3 object-cover border-2 border-white/20" />}
          <h1 className="text-2xl font-bold text-white">Support {firstName}</h1>
          <p className="text-gray-400 text-sm mt-1">Show your appreciation with a donation</p>
        </div>

        {/* Other ways to support — external donation links, shown only when 2+
            are set (they're hidden from the bio page in that case). */}
        {donateLinks.length >= 2 && (() => {
          const getPlatformDisplay = (url: string, customTitle?: string): { icon: React.ReactNode; name: string; color: string } | null => {
            const lower = (url || '').toLowerCase();
            if (lower.includes('venmo.com')) {
              return { name: customTitle || 'Venmo', color: '#3D95CE', icon: (
                <svg viewBox="0 0 24 24" className="w-4 h-4" fill="#3D95CE"><path d="M19.5 2c.914 1.523 1.323 3.094 1.323 5.074 0 6.326-5.4 14.548-9.78 20.346H3.24L0 3.46l7.76-.714 1.64 13.13C10.84 13.79 12.92 8.82 12.92 5.39c0-1.84-.36-3.13-.84-4.13L19.5 2z"/></svg>
              ) };
            }
            if (lower.includes('paypal')) {
              return { name: customTitle || 'PayPal', color: '#00457C', icon: (
                <svg viewBox="0 0 24 24" className="w-4 h-4" fill="#00457C"><path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106zm14.146-14.42a3.35 3.35 0 0 0-.607-.541c-.013.076-.026.175-.041.254-.93 4.778-4.005 7.201-9.138 7.201h-2.19a.563.563 0 0 0-.556.479l-1.187 7.527h-.506l-.24 1.516a.56.56 0 0 0 .554.647h3.882c.46 0 .85-.334.922-.788.06-.26.76-4.852.816-5.09a.932.932 0 0 1 .923-.788h.58c3.76 0 6.705-1.528 7.565-5.946.36-1.847.174-3.388-.777-4.471z"/></svg>
              ) };
            }
            if (lower.includes('cash.app')) {
              return { name: customTitle || 'Cash App', color: '#00D54B', icon: (
                <svg viewBox="0 0 24 24" className="w-4 h-4" fill="#00D54B"><path d="M3.5 3.5v17h17v-17h-17zm9.25 14.5h-1.5v-1.5h-2v-1.5h3.5v-1h-2a1.5 1.5 0 0 1-1.5-1.5v-2a1.5 1.5 0 0 1 1.5-1.5h.5V7.5h1.5V9h2v1.5h-3.5v1h2a1.5 1.5 0 0 1 1.5 1.5v2a1.5 1.5 0 0 1-1.5 1.5h-.5V18z"/></svg>
              ) };
            }
            if (lower.includes('ko-fi.com')) {
              return { name: customTitle || 'Ko-fi', color: '#FF5E5B', icon: (
                <svg viewBox="0 0 24 24" className="w-4 h-4" fill="#FF5E5B"><path d="M23.881 8.948c-.773-4.085-4.859-4.593-4.859-4.593H.723c-.604 0-.679.798-.679.798s-.082 7.324-.022 11.822c.164 2.424 2.586 2.672 2.586 2.672s8.267-.023 11.966-.049c2.438-.426 2.683-2.566 2.658-3.734 4.352.24 7.422-2.831 6.649-6.916zm-11.062 3.511c-1.246 1.453-4.011 3.976-4.011 3.976s-.121.119-.31.023c-.076-.057-.108-.09-.108-.09-.443-.441-3.368-3.049-4.034-3.954-.709-.965-1.041-2.7-.091-3.71.951-1.01 3.005-1.086 4.363.407 0 0 1.565-1.782 3.468-.963 1.904.82 1.832 3.011.723 4.311zm6.173.478c-.928.116-1.682.028-1.682.028V7.284h1.77s1.971.551 1.971 2.638c0 1.913-.985 2.667-2.059 3.015z"/></svg>
              ) };
            }
            if (lower.includes('buymeacoffee.com')) {
              return { name: customTitle || 'Buy Me a Coffee', color: '#FFDD00', icon: (
                <svg viewBox="0 0 24 24" className="w-4 h-4" fill="#FFDD00"><path d="M20.216 6.415l-.132-.666c-.119-.598-.388-1.163-1.001-1.379-.197-.069-.42-.098-.57-.241-.152-.143-.196-.366-.231-.572-.065-.378-.125-.756-.192-1.133-.057-.325-.102-.69-.25-.987-.195-.4-.597-.634-.996-.788a5.723 5.723 0 00-.626-.194c-1-.263-2.05-.36-3.077-.416a25.834 25.834 0 00-3.7.062c-.915.083-1.88.184-2.75.5-.318.116-.646.256-.888.501-.297.302-.393.77-.177 1.146.154.267.415.456.692.58.36.162.737.284 1.123.366 1.075.238 2.189.331 3.287.37 1.218.05 2.437.01 3.65-.118.299-.033.598-.073.896-.119.352-.054.578-.513.474-.834-.124-.383-.457-.531-.834-.473-.466.074-.96.108-1.382.146-1.177.08-2.358.082-3.536.006a22.228 22.228 0 01-1.157-.107c-.086-.01-.18-.025-.258-.036-.243-.036-.484-.08-.724-.13-.111-.027-.111-.185 0-.212h.005c.277-.06.557-.108.838-.147h.002c.131-.009.263-.032.394-.048a25.076 25.076 0 013.426-.12c.674.019 1.347.067 2.017.144l.228.031c.267.04.533.088.798.145.392.085.895.113 1.07.542.055.137.08.288.111.431l.319 1.484a.237.237 0 01-.199.284h-.003c-.037.006-.075.01-.112.015a36.704 36.704 0 01-4.743.295 37.059 37.059 0 01-4.699-.304c-.14-.017-.293-.042-.417-.06-.326-.048-.649-.108-.973-.161-.393-.065-.768-.032-1.123.161-.29.16-.527.404-.675.701-.154.316-.199.66-.267 1-.069.34-.176.707-.135 1.056.087.753.613 1.365 1.37 1.502a39.69 39.69 0 0011.343.376.483.483 0 01.535.53l-.071.697-1.018 9.907c-.041.41-.047.832-.125 1.237-.122.637-.553 1.028-1.182 1.171-.577.131-1.165.2-1.756.205-.656.004-1.31-.025-1.966-.022-.699.004-1.556-.06-2.095-.58-.475-.458-.54-1.174-.605-1.793l-.731-7.013-.322-3.094c-.037-.351-.286-.695-.678-.678-.336.015-.718.3-.678.679l.228 2.185.949 9.112c.147 1.344 1.174 2.068 2.446 2.272.742.12 1.503.144 2.257.156.966.016 1.942.053 2.892-.122 1.408-.258 2.465-1.198 2.616-2.657.34-3.332.683-6.663 1.024-9.995l.215-2.087a.484.484 0 01.39-.426c.402-.078.787-.212 1.074-.518.455-.488.546-1.124.385-1.766z"/></svg>
              ) };
            }
            if (lower.includes('patreon.com')) {
              return { name: customTitle || 'Patreon', color: '#FF424D', icon: (
                <svg viewBox="0 0 24 24" className="w-4 h-4" fill="#FF424D"><path d="M0 .48v23.04h4.22V.48zm15.385 0c-4.764 0-8.641 3.88-8.641 8.65 0 4.755 3.877 8.623 8.641 8.623 4.75 0 8.615-3.868 8.615-8.623C24 4.36 20.136.48 15.385.48z"/></svg>
              ) };
            }
            if (lower.includes('givesendgo.com')) {
              return { name: customTitle || 'GiveSendGo', color: '#F59E0B', icon: <span className="text-sm">🙏</span> };
            }
            if (lower.includes('gofundme.com')) {
              return { name: customTitle || 'GoFundMe', color: '#00B964', icon: <span className="text-sm">💚</span> };
            }
            return null;
          };

          return (
            <div className="mb-6">
              <p className="text-gray-400 text-xs font-medium mb-2 text-center">Other ways to support {firstName}</p>
              <div className="flex justify-center gap-2 flex-wrap">
                {donateLinks.map((link) => {
                  const platform = getPlatformDisplay(link.url, link.title || undefined);
                  const displayTitle = link.title || platform?.name || 'Support';
                  return (
                    <a
                      key={link.id}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/5 border transition-all hover:scale-105 hover:bg-white/10"
                      style={{ borderColor: platform ? platform.color + '66' : 'rgba(255,255,255,0.2)' }}
                    >
                      {platform?.icon}
                      <span className="text-xs font-medium text-white/90">{displayTitle}</span>
                    </a>
                  );
                })}
              </div>
            </div>
          );
        })()}

        {/* Personal Message (hidden when coming from message page) */}
        {!skipMessage && (
          <div className="mb-4">
            <label className="text-white text-sm font-medium mb-2 block">Personal Message To {firstName}</label>
            <textarea
              value={personalMessage}
              onChange={e => setPersonalMessage(e.target.value)}
              placeholder={`Write a message to ${firstName}...`}
              maxLength={500}
              rows={3}
              className="w-full bg-white/5 border border-white/20 px-4 py-3 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-white/40 resize-none text-sm"
            />
            {personalMessage.length > 0 && (
              <p className="text-gray-500 text-xs text-right mt-1">{personalMessage.length}/500</p>
            )}
          </div>
        )}

        {/* Amount */}
        <div className="mb-4">
          <div className="grid grid-cols-4 gap-2 mb-3">
            {SUGGESTED_AMOUNTS.map(a => (
              <button key={a} onClick={() => setAmount(String(a))}
                className={`py-3 rounded-xl text-sm font-bold transition-colors ${amount === String(a) ? 'bg-green-500 text-white' : 'bg-white/5 text-gray-300 border border-white/10 hover:bg-white/10'}`}>
                ${a}
              </button>
            ))}
          </div>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg">$</span>
            <input type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="Custom amount" min="1"
              className="w-full bg-white/5 border border-white/20 pl-8 pr-4 py-3 rounded-xl text-white text-lg placeholder-gray-500 focus:outline-none focus:border-white/40" />
          </div>
        </div>

        {/* Monthly toggle */}
        <div className="p-3 rounded-xl bg-white/5 border border-white/10 mb-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white text-sm font-medium">Support {firstName} monthly</p>
              <p className="text-green-400 text-xs">🎁 Get 10% off ShoutOuts from {firstName}</p>
            </div>
            <button type="button" onClick={() => { setIsMonthly(!isMonthly); if (!isMonthly) setUseCredits(false); }}
              className={`w-12 h-6 rounded-full transition-colors ${isMonthly ? 'bg-green-500' : 'bg-gray-600'}`}>
              <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${isMonthly ? 'translate-x-6' : 'translate-x-0.5'}`} />
            </button>
          </div>
          {isMonthly && (
            <div className="mt-2 pt-2 border-t border-white/10">
              <span className="inline-flex items-center gap-1 text-xs text-green-400 bg-green-400/10 px-2 py-1 rounded-full">Billed monthly. Cancel anytime.</span>
            </div>
          )}
        </div>

        {/* Fee breakdown */}
        {donationAmount > 0 && (
          <div className="p-3 rounded-xl bg-white/5 border border-white/10 mb-4 text-sm space-y-1">
            <div className="flex justify-between"><span className="text-gray-400">Donation</span><span className="text-white">${donationAmount.toFixed(2)}</span></div>
            <div className="flex justify-between"><span className="text-gray-400">Processing fee (2.9% + $0.30)</span><span className="text-white">${processingFee.toFixed(2)}</span></div>
            {creditsAvailable > 0 && <div className="flex justify-between"><span className="text-green-400">Credits applied</span><span className="text-green-400">-${creditsAvailable.toFixed(2)}</span></div>}
            <div className="flex justify-between border-t border-white/10 pt-1"><span className="text-white font-medium">Total</span><span className="text-white font-bold">${amountToPay > 0 ? amountToPay.toFixed(2) : '0.00'}</span></div>
          </div>
        )}

        {/* Email */}
        {!emailValid ? (
          <div className="mb-4">
            <input
              type="email"
              inputMode="email"
              autoComplete="email"
              enterKeyHint="go"
              value={email}
              onChange={e => { setEmail(e.target.value); setEmailTouched(false); }}
              onBlur={handleEmailBlur}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  (e.target as HTMLInputElement).blur();
                  void handleEmailBlur();
                }
              }}
              placeholder="Enter your email"
              className="w-full bg-white/5 border border-white/20 px-4 py-3 rounded-xl text-white placeholder-gray-500 focus:outline-none"
            />
            {email && emailTouched && !emailValid && (
              <p className="text-red-400 text-xs mt-1">Please enter a valid email address</p>
            )}
            {/* Mobile keyboards' "Done" key doesn't reliably blur — give an
                explicit Continue affordance so the form can advance. */}
            {email.trim().length > 0 && (
              <button
                type="button"
                onClick={() => void handleEmailBlur()}
                className="mt-3 w-full px-4 py-3 rounded-xl text-white font-medium transition-opacity"
                style={{ background: accentColor }}
              >
                Continue
              </button>
            )}
          </div>
        ) : (
          <p className="text-gray-400 text-sm mb-4">Supporting as <span className="text-white font-medium">{email}</span>
            <button onClick={() => { setEmailValid(false); setSavedCards([]); setSelectedCardId(null); setCommerceInstance(null); setIsPaymentReady(false); setUseNewCard(false); setIsVerified(false); }} className="text-blue-400 ml-2 text-xs">change</button>
          </p>
        )}

        {/* Credits — ONLY for verified users */}
        {!isMonthly && isVerified && userCredits > 0 && emailValid && (
          <div className="flex items-center justify-between p-3 rounded-xl bg-white/5 border border-white/10 mb-4">
            <div>
              <p className="text-white text-sm font-medium">Use ShoutOut Credits</p>
              <p className="text-gray-400 text-xs">Balance: ${userCredits.toFixed(2)}</p>
            </div>
            <button type="button" onClick={() => setUseCredits(!useCredits)} className={`w-12 h-6 rounded-full transition-colors ${useCredits ? 'bg-green-500' : 'bg-gray-600'}`}>
              <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${useCredits ? 'translate-x-6' : 'translate-x-0.5'}`} />
            </button>
          </div>
        )}

        {/* Anonymous toggle */}
        {emailValid && (
          <div className="flex items-center justify-between p-3 rounded-xl bg-white/5 border border-white/10 mb-4">
            <div>
              <p className="text-white text-sm font-medium">Make my support anonymous</p>
              <p className="text-gray-400 text-xs">Your name won't be shown publicly</p>
            </div>
            <button type="button" onClick={() => setIsAnonymous(!isAnonymous)} className={`w-12 h-6 rounded-full transition-colors ${isAnonymous ? 'bg-green-500' : 'bg-gray-600'}`}>
              <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${isAnonymous ? 'translate-x-6' : 'translate-x-0.5'}`} />
            </button>
          </div>
        )}

        {/* Saved cards — ONLY for verified users */}
        {amountToPay > 0 && emailValid && isVerified && savedCards.length > 0 && !useNewCard && (
          <div className="mb-4">
            <p className="text-white text-sm font-medium mb-2">Payment method</p>
            <div className="space-y-2">
              {savedCards.map((card: any) => (
                <button key={card.id} onClick={() => setSelectedCardId(card.id)}
                  className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border transition-colors ${selectedCardId === card.id ? 'border-white/40 bg-white/10' : 'border-white/10 bg-white/5'}`}>
                  <div className="flex items-center gap-3">
                    <span>💳</span>
                    <span className="text-white text-sm">{(card.sourceType || 'Card').toUpperCase()} ****{card.lastDigits}</span>
                  </div>
                  {selectedCardId === card.id && <span className="text-green-400">✓</span>}
                </button>
              ))}
            </div>
            <button onClick={() => { setUseNewCard(true); setCommerceInstance(null); setIsPaymentReady(false); }} className="text-blue-400 text-xs mt-2 hover:text-blue-300">Add a new card</button>

            <button onClick={handlePayWithSavedCard}
              disabled={processing || !donationAmount || donationAmount < 1 || !selectedCardId}
              className="w-full mt-4 py-4 rounded-xl font-bold text-lg text-white bg-green-600 hover:bg-green-700 disabled:opacity-40 transition-colors flex items-center justify-center gap-2">
              {processing ? <><div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" /> Processing...</> : `Support ${firstName} $${amountToPay.toFixed(2)}`}
            </button>
          </div>
        )}

        {/* Fortis card form — for guests or "add new card" */}
        {showFortisForm && (
          <div className="mb-4">
            <p className="text-gray-500 text-xs mb-3">We save this card securely through LunarPay for your convenience and future orders with {firstName}.</p>
            <div className="relative w-full rounded-2xl border border-white/10 overflow-hidden px-4 pb-4" style={{ backgroundColor: gradStart }}>
              {!isPaymentReady && (
                <div className="absolute inset-0 flex items-center justify-center z-10 rounded-2xl" style={{ backgroundColor: gradStart }}>
                  <div className="flex flex-col items-center gap-3">
                    <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-green-500" />
                    <span className="text-gray-400 text-sm">Loading payment form...</span>
                  </div>
                </div>
              )}
              <style>{`
                #support-payment-container button[type="submit"],
                #support-payment-container .submit-button,
                #support-payment-container [class*="SubmitButton"],
                #support-payment-container [class*="submit-btn"],
                #support-payment-container > div > button,
                #support-payment-container h1,
                #support-payment-container h2,
                #support-payment-container h3,
                #support-payment-container [class*="Header"],
                #support-payment-container [class*="header"],
                #support-payment-container [class*="Title"]:first-child,
                #support-payment-container [class*="PaymentInfo"],
                #support-payment-container [class*="payment-info"] {
                  display: none !important;
                }
                #support-payment-container {
                  overflow: hidden;
                  border-radius: 12px;
                }
              `}</style>
              <div id="support-payment-container" ref={iframeContainerRef} className="min-h-[280px] w-full" style={{ marginTop: '-70px' }} />
            </div>

            {/* Our own submit button */}
            {isPaymentReady && donationAmount >= 1 && (
              <button
                type="button"
                onClick={() => { if (commerceInstance) { setProcessing(true); commerceInstance.submit(); } }}
                disabled={processing}
                className="w-full mt-4 py-4 rounded-xl font-bold text-lg text-white bg-green-600 hover:bg-green-700 disabled:opacity-40 transition-colors flex items-center justify-center gap-2">
                {processing ? <><div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" /> Processing...</> : `Support ${firstName} $${amountToPay.toFixed(2)}`}
              </button>
            )}

            {savedCards.length > 0 && (
              <button onClick={() => { setUseNewCard(false); setCommerceInstance(null); setIsPaymentReady(false); }} className="text-gray-400 text-xs mt-2 hover:text-white">Use saved card instead</button>
            )}
          </div>
        )}

        {/* Credits-only button */}
        {amountToPay === 0 && creditsAvailable > 0 && emailValid && isVerified && (
          <button
            onClick={async () => {
              setProcessing(true);
              try { await createDonationOrder(Math.round(totalCharge * 100), 0, 'CREDITS_ONLY', isMonthly, donationAmount, personalMessage); }
              catch (e: any) { toast.error(e.message || 'Failed'); }
              finally { setProcessing(false); }
            }}
            disabled={processing}
            className="w-full py-4 rounded-xl font-bold text-lg text-white bg-green-600 hover:bg-green-700 disabled:opacity-40 transition-colors flex items-center justify-center gap-2">
            {processing ? <><div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" /> Processing...</> : `Support ${firstName} with Credits`}
          </button>
        )}

        {isMonthly && <p className="text-gray-500 text-xs text-center mt-2">You will be charged ${totalCharge.toFixed(2)}/month. Cancel anytime from your account.</p>}

        {/* Supporter Feed */}
        {supporterMessages.length > 0 && (
          <div className="mt-8 pt-6 border-t border-white/10">
            <h3 className="text-white font-semibold text-sm mb-3">Messages & Supporters</h3>
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {supporterMessages.map((m, i) => (
                <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-white text-sm font-medium">
                      {m.is_anonymous ? 'Anonymous' : (m.donor_name || 'Supporter')}
                    </span>
                    <div className="flex items-center gap-2">
                      {m.amount > 0 && (
                        <span className="text-green-400 text-xs font-medium">${parseFloat(m.amount).toFixed(0)}</span>
                      )}
                      <span className="text-gray-500 text-xs">
                        {new Date(m.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </span>
                    </div>
                  </div>
                  <p className="text-gray-300 text-sm">{m.message}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="pt-6 text-center"><p className="text-gray-600 text-xs">Powered by ShoutOut</p></div>
      </div>

      {/* OTP Modal */}
      {showOtpModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gray-900 border border-white/10 rounded-2xl p-6 w-full max-w-sm">
            <h3 className="text-lg font-bold text-white text-center mb-1">Welcome back!</h3>
            <p className="text-gray-400 text-sm text-center mb-5">Verify to access your saved cards</p>
            {!otpSent && otpNeedsPhone ? (
              <div className="space-y-3">
                <input type="tel" value={otpPhone} onChange={e => setOtpPhone(e.target.value)} placeholder="Phone number"
                  className="w-full bg-white/5 border border-white/20 px-4 py-3 rounded-xl text-white placeholder-gray-500 focus:outline-none text-center text-lg" />
                <button onClick={handleOtpSend} disabled={otpLoading}
                  className="w-full py-3 rounded-xl font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2">
                  {otpLoading ? <><div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" /> Sending...</> : 'Send Code'}
                </button>
              </div>
            ) : otpSent ? (
              <div className="space-y-3">
                <p className="text-gray-400 text-sm text-center">Code sent to {otpPhoneHint || '***'}</p>
                <input type="text" value={otpCode} onChange={e => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000" maxLength={6} autoFocus
                  className="w-full bg-white/5 border border-white/20 px-4 py-3 rounded-xl text-white text-center text-2xl tracking-[0.5em] placeholder-gray-500 focus:outline-none font-mono"
                  onKeyDown={e => { if (e.key === 'Enter') handleOtpVerify(); }} />
                <button onClick={handleOtpVerify} disabled={otpLoading}
                  className="w-full py-3 rounded-xl font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2">
                  {otpLoading ? <><div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" /> Verifying...</> : 'Verify & Continue'}
                </button>
                <button onClick={() => { setOtpSent(false); setOtpCode(''); setOtpNeedsPhone(true); }}
                  className="w-full text-center text-sm text-gray-400 hover:text-white">Change number</button>
              </div>
            ) : (
              <div className="flex items-center justify-center py-4">
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-blue-500 border-t-transparent" />
              </div>
            )}
            <button onClick={() => setShowOtpModal(false)}
              className="w-full mt-4 text-center text-sm text-gray-500 hover:text-white">Skip — pay with new card</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupportPage;
